import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const services = [
  {
    id: 'commercial',
    numeral: 'i',
    title: 'Commercial Electrical',
    tagline: 'Ground-up & fit-out, delivered on schedule.',
    description: 'Full-scope electrical installations for offices, retail spaces, and commercial buildings—designed for reliability and code compliance. We handle everything from initial rough-in to final trim.',
    details: 'Retail, hospitality, restaurants, office, mixed-use. From shell construction to tier-one tenant improvements, we manage every trade, every inspection, every deliverable.',
    items: ['Restaurants & hospitality', 'Retail & franchise rollouts', 'Office & mixed-use', 'Tenant improvements'],
    image: 'https://images.unsplash.com/photo-1584259097630-4d5cb684a0ec?w=800&q=80',
  },
  {
    id: 'residential',
    numeral: 'ii',
    title: 'Residential Electrical',
    tagline: 'Custom homes and multi-unit, finished to a standard.',
    description: 'Custom homes, townhomes, low-rise condominiums, and purpose-built rentals. We work alongside owners, architects, and developers from foundation through occupancy.',
    details: 'Complete electrical systems for new construction and renovations. Panel upgrades, rewiring, smart home integration, and energy-efficient lighting solutions.',
    items: ['Custom homes', 'Townhomes & low-rise', 'Multi-unit residential', 'Renovation & additions'],
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80',
  },
  {
    id: 'industrial',
    numeral: 'iii',
    title: 'Industrial Electrical',
    tagline: 'Built for the floor it has to perform on.',
    description: 'Heavy-duty electrical systems for warehouses, manufacturing plants, and industrial facilities with proper load management and safety systems.',
    details: 'Distribution centres, cold storage, warehouses, manufacturing facilities. Racking, dock equipment, ESFR systems, heavy slabs. The unglamorous work that keeps a business moving.',
    items: ['Warehousing & distribution', 'Cold storage', 'Manufacturing facilities', 'High-voltage systems'],
    image: 'https://images.unsplash.com/photo-1581093458791-9d42e3c2fd45?w=800&q=80',
  },
  {
    id: 'panels',
    numeral: 'iv',
    title: 'Panel Upgrades & Distribution',
    tagline: 'Engineered for your growing demands.',
    description: 'Electrical panel replacements, upgrades, and distribution systems engineered for your facility\'s growing demands.',
    details: 'We assess your current capacity and design solutions that accommodate future expansion while meeting all safety codes and ESA requirements.',
    items: ['Panel replacements', 'Capacity upgrades', 'Load balancing', 'Code compliance'],
    image: 'https://images.unsplash.com/photo-1607631697491-61972eecf928?w=800&q=80',
  },
  {
    id: 'lighting',
    numeral: 'v',
    title: 'Lighting & Controls',
    tagline: 'Energy efficiency meets aesthetics.',
    description: 'Interior and exterior lighting design, installation, and automated controls for energy efficiency and visual appeal.',
    details: 'We offer LED retrofits, smart lighting systems, and custom solutions that reduce energy costs while enhancing your space.',
    items: ['LED retrofits', 'Smart lighting systems', 'Automated controls', 'Emergency lighting'],
    image: 'https://images.unsplash.com/photo-1565538810643-b5bdb714032a?w=800&q=80',
  },
  {
    id: 'ev-charging',
    numeral: 'vi',
    title: 'EV Charging',
    tagline: 'Infrastructure for the future.',
    description: 'EV charging station installation and infrastructure planning for residential and multi-unit facilities.',
    details: 'We handle permit applications, electrical upgrades, and charger installation for both Level 2 and DC fast charging systems.',
    items: ['Level 2 chargers', 'DC fast charging', 'Multi-unit installations', 'Permit management'],
    image: 'https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=800&q=80',
  },
];

export default function ServicesPage() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  return (
    <main className="pt-20" data-testid="services-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#3D3A38]">
        <div className="container-custom">
          <p className="section-label mb-4 text-[#F5F1EC]/60">Services</p>
          <h1 
            className="text-4xl md:text-5xl lg:text-6xl text-[#F5F1EC] mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}
          >
            Electrical services for every <span className="italic text-[#B8A066]">sector.</span>
          </h1>
          <p 
            className="text-lg text-[#F5F1EC]/60 max-w-2xl"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            We do not subcontract our judgment. We manage every project with care, expertise, and dedication — whatever the sector.
          </p>
        </div>
      </section>

      {/* Services List */}
      <section className="py-24 md:py-32 bg-[#F5F1EC]">
        <div className="container-custom">
          <div className="space-y-24">
            {services.map((service, index) => (
              <div 
                key={service.id}
                id={service.id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                style={{ scrollMarginTop: '120px' }}
              >
                {/* Image */}
                <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="relative h-[400px] overflow-hidden">
                    <img 
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className={`flex flex-col justify-center ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="flex items-start gap-3 mb-4">
                    <span className="roman-numeral text-lg">{service.numeral}</span>
                    <h2 
                      className="text-3xl md:text-4xl text-[#3D3A38]"
                      style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}
                    >
                      {service.title}
                    </h2>
                  </div>

                  <p 
                    className="text-lg text-[#B8A066] italic mb-6"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  >
                    {service.tagline}
                  </p>

                  <p 
                    className="text-[#5C5855] mb-4 leading-relaxed"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {service.description}
                  </p>

                  <p 
                    className="text-sm text-[#5C5855]/80 mb-8 leading-relaxed"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {service.details}
                  </p>

                  {/* Service Items */}
                  <ul className="space-y-2 mb-8">
                    {service.items.map((item, idx) => (
                      <li 
                        key={idx}
                        className="text-xs tracking-[0.1em] uppercase text-[#5C5855] flex items-center gap-3"
                        style={{ fontFamily: 'Outfit, sans-serif' }}
                      >
                        <span className="w-6 h-px bg-[#B8A066]" />
                        {item}
                      </li>
                    ))}
                  </ul>

                  <Link 
                    to="/contact"
                    className="btn-primary inline-flex items-center gap-2 self-start"
                    data-testid={`service-cta-${service.id}`}
                  >
                    Get a Quote <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#3D3A38]">
        <div className="container-custom text-center">
          <h2 
            className="text-3xl md:text-4xl text-[#F5F1EC] mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}
          >
            Ready to start your <span className="italic text-[#B8A066]">project?</span>
          </h2>
          <p 
            className="text-[#F5F1EC]/60 mb-8 max-w-xl mx-auto"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Contact us today for a consultation and quote. We respond within 24 hours.
          </p>
          <Link 
            to="/contact"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="services-final-cta"
          >
            Request a Quote <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </main>
  );
}
